import { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { Box, InputAdornment, OutlinedInput } from '@mui/material';
import { Search } from 'lucide-react';

import { buildCountryFlags } from '@modules/_shared/utils/countryQueryHelpers';
import { useScopedSearchParams } from '@modules/_shared/utils/navigation';
import { MAX_GRAPHQL_INT } from '@modules/_shared/utils/paginations';
import {
  FIND_CMA_LISTING_BY_PORTAL_ID,
  FIND_CMA_LISTING_BY_RAW_IDENTIFIER,
} from '@modules/reports/cma-reports/cmaReportsQueries';
import { RENTAL_CMA_LISTING_LOOKUP } from '@modules/reports/cma-reports/rentalQueries';
import {
  getRentalCountryRows,
  getRentalCountryVariables,
} from '@modules/reports/cma-reports/step-listing-compare/rentalComparablesSearch';
import { parseListingUrl } from '@modules/reports/cma-reports/step-listing-compare/utils/parseListingUrl';
import { useDebouncedValue } from '@shared/hooks/debounce';
import { useLocale } from '@shared/hooks/locale';

import { PINNED_LISTING_ID_PARAM } from './ComparableListings';

const DEBOUNCE_MS = 350;
// Sentinel pin: forces the grid to zero rows so its "No results found"
// empty state shows instead of unrelated proximity rows.
const NO_MATCH_PIN = '0';

// Above this many matches the raw input is treated as ambiguous noise; we
// fetch one extra row to detect overflow without a separate count query.
const RAW_MATCH_LIMIT = 20;

type UrlSearchProps = {
  offerType?: 'sell' | 'rent';
  queryParamsScope?: string;
  onEmptyMessageChange?: (message: string | null) => void;
  onLoadingChange?: (loading: boolean) => void;
};

export const UrlSearch = ({
  offerType = 'sell',
  queryParamsScope,
  onEmptyMessageChange,
  onLoadingChange,
}: UrlSearchProps) => {
  const { t, countryCode } = useLocale();
  const [searchParams, setSearchParams] =
    useScopedSearchParams(queryParamsScope);

  const [rawInput, setRawInput] = useState('');
  const debouncedInput = useDebouncedValue(rawInput.trim(), DEBOUNCE_MS);

  const isUrl = /^https?:\/\//i.test(debouncedInput);

  const parsed = useMemo(() => {
    if (!isUrl) {
      return null;
    }
    return parseListingUrl(debouncedInput, countryCode);
  }, [debouncedInput, isUrl, countryCode]);

  const canLookupUrl = parsed != null && parsed.ok;
  const canLookupRaw = !isUrl && debouncedInput !== '';

  const errorMessage = useMemo<string | null>(() => {
    if (parsed == null || parsed.ok) {
      return null;
    }
    switch (parsed.error.kind) {
      case 'wrong_country':
        return t(
          'This is a {{country}} portal — open it in a {{country}} CMA.',
          {
            country: parsed.error.portalCountry,
          },
        );
      case 'unsupported_portal':
        return t("We don't track listings from this site.");
      case 'no_id_found':
        return t("Couldn't read a listing ID from this link.");
      case 'invalid_url':
        return null;
    }
  }, [parsed, t]);

  const countryFlags = useMemo(
    () => buildCountryFlags(countryCode),
    [countryCode],
  );

  const urlVariables =
    parsed != null && parsed.ok
      ? {
          portal: parsed.value.portal,
          originalId: parsed.value.originalId,
          ...countryFlags,
        }
      : { portal: '', originalId: '', ...countryFlags };

  const { data: urlLookupData, loading: urlLookupLoading } = useQuery(
    FIND_CMA_LISTING_BY_PORTAL_ID,
    {
      skip: offerType === 'rent' || !canLookupUrl,
      variables: urlVariables,
      context: { clientName: 'scrapers' },
      fetchPolicy: 'cache-and-network',
    },
  );

  const { data: rawLookupData, loading: rawLookupLoading } = useQuery(
    FIND_CMA_LISTING_BY_RAW_IDENTIFIER,
    {
      skip: offerType === 'rent' || !canLookupRaw,
      variables: {
        raw: debouncedInput,
        limit: RAW_MATCH_LIMIT + 1,
        ...countryFlags,
      },
      context: { clientName: 'scrapers' },
      fetchPolicy: 'cache-and-network',
    },
  );

  const { data: rentalLookupData, loading: rentalLookupLoading } = useQuery(
    RENTAL_CMA_LISTING_LOOKUP,
    {
      skip: offerType !== 'rent' || (!canLookupUrl && !canLookupRaw),
      context: { clientName: 'scrapers' },
      fetchPolicy: 'cache-and-network',
      variables: {
        limit: RAW_MATCH_LIMIT + 1,
        ...getRentalCountryVariables(countryCode, {
          offer_type: { _eq: 'rent' },
          ...(parsed?.ok
            ? {
                portal: { _eq: parsed.value.portal },
                original_id: { _eq: parsed.value.originalId },
              }
            : {
                _or: [
                  { original_id: { _eq: debouncedInput } },
                  { agency_reference: { _eq: debouncedInput } },
                  ...(/^\d+$/.test(debouncedInput) &&
                  Number(debouncedInput) <= MAX_GRAPHQL_INT
                    ? [{ id: { _eq: Number(debouncedInput) } }]
                    : []),
                ],
              }),
        }),
      },
    },
  );

  // [] = no match (→ sentinel); null = not settled yet.
  const matchedIds = useMemo<number[] | null>(() => {
    if (offerType === 'rent') {
      if (!canLookupUrl && !canLookupRaw) {
        return null;
      }
      if (rentalLookupData == null) {
        return null;
      }
      const rows = getRentalCountryRows(rentalLookupData);
      return rows.length > RAW_MATCH_LIMIT ? [] : rows.map(row => row.id);
    }
    if (canLookupUrl) {
      if (urlLookupData == null) {
        return null;
      }
      const row =
        urlLookupData.ch_cma_listings?.[0] ??
        urlLookupData.es_cma_listings?.[0] ??
        urlLookupData.fr_cma_listings?.[0] ??
        urlLookupData.it_cma_listings?.[0] ??
        urlLookupData.pl_cma_listings?.[0] ??
        urlLookupData.pt_cma_listings?.[0] ??
        null;
      const id = row?.id;
      return id != null ? [id] : [];
    }

    if (canLookupRaw) {
      if (rawLookupData == null) {
        return null;
      }
      const rows =
        rawLookupData.ch_cma_listings ??
        rawLookupData.es_cma_listings ??
        rawLookupData.fr_cma_listings ??
        rawLookupData.it_cma_listings ??
        rawLookupData.pl_cma_listings ??
        rawLookupData.pt_cma_listings ??
        [];
      if (rows.length > RAW_MATCH_LIMIT) {
        return [];
      }
      return rows.map(r => r.id).filter((id): id is number => id != null);
    }

    return null;
  }, [
    canLookupUrl,
    canLookupRaw,
    urlLookupData,
    rawLookupData,
    offerType,
    rentalLookupData,
  ]);

  const noMatchMessage =
    errorMessage == null &&
    matchedIds != null &&
    matchedIds.length === 0 &&
    debouncedInput !== ''
      ? t('No matching listing was found.')
      : null;

  const trimmedInput = rawInput.trim();
  const lookupActive = canLookupUrl || canLookupRaw;
  const lookupPending =
    lookupActive &&
    (urlLookupLoading ||
      rawLookupLoading ||
      rentalLookupLoading ||
      matchedIds == null);
  const resolvedPin =
    matchedIds != null && matchedIds.length > 0
      ? matchedIds.join(',')
      : NO_MATCH_PIN;
  const searchLoading =
    trimmedInput !== '' &&
    (trimmedInput !== debouncedInput ||
      lookupPending ||
      searchParams.get(PINNED_LISTING_ID_PARAM) !== resolvedPin);

  useEffect(() => {
    onEmptyMessageChange?.(errorMessage ?? noMatchMessage);
  }, [errorMessage, noMatchMessage, onEmptyMessageChange]);

  useEffect(() => {
    onLoadingChange?.(searchLoading);
  }, [onLoadingChange, searchLoading]);

  useEffect(
    () => () => {
      onLoadingChange?.(false);
    },
    [onLoadingChange],
  );

  // Drive the grid via `pinned_listing_id`: clear on empty input, pin the
  // sentinel when unresolved, pin the resolved ids when matched.
  // Pin intentionally persists on unmount: ListToolbar closes this input on any
  // outside click (e.g. selecting a comparable), so clearing it would drop the filter.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const currentPin = next.get(PINNED_LISTING_ID_PARAM);

    if (debouncedInput === '') {
      if (currentPin != null) {
        next.delete(PINNED_LISTING_ID_PARAM);
        next.set('offset', '0');
        setSearchParams(next);
      }
      return;
    }

    if (lookupPending) {
      return;
    }

    if (currentPin === resolvedPin) {
      return;
    }
    next.set(PINNED_LISTING_ID_PARAM, resolvedPin);
    next.set('offset', '0');
    setSearchParams(next);
  }, [
    matchedIds,
    debouncedInput,
    lookupPending,
    resolvedPin,
    searchParams,
    setSearchParams,
  ]);

  return (
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <OutlinedInput
        autoFocus
        autoComplete="off"
        fullWidth
        size="small"
        value={rawInput}
        onChange={e => setRawInput(e.target.value)}
        placeholder={t('URL, listing ID, or agency reference')}
        startAdornment={
          <InputAdornment position="start">
            <Search size={16} />
          </InputAdornment>
        }
        sx={{ background: 'white' }}
      />
    </Box>
  );
};

export default UrlSearch;
